import {
    Button, ButtonGroup, Card, CardContent, CardMedia, Dialog, DialogContent, DialogTitle, List, ListItem, ListItemIcon,
    ListItemText, TextareaAutosize, TextField, Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import DoneIcon from '@mui/icons-material/Done';
import { Link, useNavigate } from "react-router-dom";
import { patient, takenTreament, user } from "../common/type";
import { getTakenTreatments } from "../services/get.service";
import ClipLoader from "react-spinners/ClipLoader";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { postFee, postPatientHistory } from "../services/post.service";
import { yupResolver } from "@hookform/resolvers/yup";
import { transaction } from "../validations/Validation";

interface PaymentData {
    amount: number;
    toWhom: number;
    description: string;
    purpose: string;
}

const SinglePatient = (props: {user: user | null; setTakenTreatment: (takenTreament: takenTreament) => void; patient: patient | null}) => {
    const { patient, setTakenTreatment, user } = props
    const navigate = useNavigate()
    const [takenTreatments, setTakenTreatments] = useState<takenTreament[]>([])
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [open2, setOpen2] = useState(false)
    const currencyFormatter = new Intl.NumberFormat();

    useEffect(() => {
        setLoading(true)

        getTakenTreatments(patient ? patient.id : 0)
            .then((res) => {
                setLoading(false)
                setTakenTreatments(res.data)
            })
            .catch((err) => {
                setLoading(false)
                console.log(err);
            })
    }, [patient])

    const { register, handleSubmit } = useForm();
    const { register: register1, handleSubmit: handleSubmit1, formState: { errors }, reset } = useForm<PaymentData>({
        resolver: yupResolver(transaction)
    });

    const onSubmit1 = (data: PaymentData) => {
        setOpen2(false)
        const load = toast.loading("Please wait...", {
            theme: "colored",
            type: "info",
            closeButton: true,
        })

        postFee(data)
            .then((res) => {
                toast.update(load, {
                    render: res.data,
                    type: "success",
                    theme: "colored",
                    autoClose: 2500,
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });
                navigate('/patient')
            })
            .catch((err) => {
                console.log(err);
                toast.update(load, {
                    render: err?.response?.data || "Something went wrong",
                    theme: "colored",
                    autoClose: 5000,
                    type: "error",
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });
            })
    }

    const OnSubmit = (data: any) => {
        var id = toast.loading("Please wait...", {
            theme: "colored",
            type: "info",
            closeButton: true,
        });

        postPatientHistory(data)
            .then(() => {
                toast.update(id, {
                    render: "Added Successfully",
                    type: "success",
                    theme: "colored",
                    autoClose: 2500,
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });

                setLoading(false);
                navigate("/search/patient");
            })
            .catch((err) => {
                toast.update(id, {
                    render: err?.response?.data?.detail,
                    theme: "colored",
                    autoClose: 5000,
                    type: "error",
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });
                setLoading(false);
                console.log(err);
            });
    }

    return <>
        <Helmet><title>Patient</title></Helmet>
        <div className="dark:text-black w-full">
            <div className="lg:m-12 m-4">
                <div className="bg-amber50 shadow-md rounded w-full lg:px-20 px-2 py-10 pt-5 lg:grid lg:grid-cols-3 gap-5 flex flex-col gap-5">
                    <div className="flex flex-col">
                        <Card>
                            <CardContent>
                                <Typography align="center">Medical History</Typography>
                                {
                                    patient?.history === null && user?.roles.some((role) => role.name === 'Doctor' || role.name === 'Admin')
                                        ?
                                        <Button onClick={() => setOpen(true)} >Add History</Button>
                                        :
                                        <></>
                                }
                                <div className="grid grid-cols-2 gap-2 py-3">
                                    {
                                        patient ?
                                            <>
                                                <span className="text-xs"><span className="font-bold">CC:</span> {patient?.history?.cc
                                                    ? patient?.history.cc
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">HPI:</span> {patient?.history?.hpi
                                                    ? patient?.history.hpi
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">PMHY:</span> {patient?.history?.pmhy
                                                    ? patient?.history.pmhy
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">PDHY:</span> {patient?.history?.pdhy
                                                    ? patient?.history.pdhy
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">GA:</span> {patient?.history?.ga
                                                    ? patient?.history.ga
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">IO:</span> {patient?.history?.io
                                                    ? patient?.history.io
                                                    : "----------"}</span>
                                                <span className="text-xs"><span className="font-bold">EO:</span> {patient?.history?.eo
                                                    ? patient?.history.eo
                                                    : "-----------"}</span>
                                                <span className="text-xs"><span className="font-bold">Asst:</span> {patient?.history?.asst
                                                    ? patient?.history.asst
                                                    : "-----------"}</span>
                                                <span className="text-xs"><span className="font-bold">TyPlan:</span> {patient?.history?.tyPlan
                                                    ? patient?.history.tyPlan
                                                    : "----------"}</span>
                                            </>
                                            :
                                            <>--------------</>
                                    }
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Typography align="center">Risk Factors and Habits</Typography>
                                <div className="grid grid-cols-2 gap-2 py-3">
                                    <div>
                                        <Typography align="center" className="underline">Risk Factors</Typography>
                                        {
                                            patient ?
                                                <List dense={true}>
                                                    {
                                                        patient.risks !== "" && patient.risks !== null ?
                                                            patient.risks.split(',').map((risk) => (
                                                                <ListItem>
                                                                    <ListItemIcon><DoneIcon /></ListItemIcon>
                                                                    <ListItemText>{risk}</ListItemText>
                                                                </ListItem>
                                                            ))
                                                            :
                                                            <>--------------</>
                                                    }
                                                </List>
                                                :
                                                <>--------------</>
                                        }
                                    </div>
                                    <div>
                                        <Typography align="center" className="underline">Habits</Typography>
                                        {
                                            patient ?
                                                <List dense={true}>
                                                    {
                                                        patient.habits !== "" && patient.habits !== null ?
                                                            patient.habits.split(',').map((risk) => (
                                                                <ListItem>
                                                                    <ListItemIcon><DoneIcon /></ListItemIcon>
                                                                    <ListItemText>{risk}</ListItemText>
                                                                </ListItem>
                                                            ))
                                                            :
                                                            <>--------------</>
                                                    }
                                                </List>
                                                :
                                                <>--------------</>
                                        }
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="lg:px-5 flex flex-col gap-3">
                        {
                            user?.roles.some((role) => role.name === 'Doctor' || role.name === 'Admin' || role.name === 'Cashier')
                                ?
                                <ButtonGroup className="">
                                    {
                                        user?.roles.some((role) => role.name === 'Doctor' || role.name === 'Admin') &&
                                        <Button variant="outlined">
                                            <Link to="/add/treatment">Add New Treatment</Link>
                                        </Button>
                                    }
                                    <Button variant="outlined" onClick={() => {reset(); setOpen2(true)}}>Add Card Fee</Button>
                                </ButtonGroup>
                                :
                                <></>
                        }
                        {
                            loading ?
                                <ClipLoader />
                                :
                                <div style={{ overflowY: "scroll", maxHeight: 450 }} className="grid gap-3">
                                    {takenTreatments.length > 0 ?
                                        takenTreatments.sort((a, b) => b.id - a.id).map((takenTreatment) => (
                                            <>
                                                <Card className="hover:bg-gray-200 hover:cursor-pointer" onClick={() => { setTakenTreatment(takenTreatment); navigate('taken/treatment') }}>
                                                    <CardContent>
                                                        <Typography variant="h6">{takenTreatment.treatmentName}</Typography>
                                                        <span>Remaining Payment -&gt; {currencyFormatter.format(takenTreatment.price - takenTreatment.total)} Birr</span>
                                                    </CardContent>
                                                </Card>
                                            </>
                                        ))

                                        :
                                        <Card className="hover:bg-red-200">
                                            <CardContent>
                                                <Typography variant="h6">No Treatment Added</Typography>
                                            </CardContent>
                                        </Card>
                                    }
                                </div>
                        }
                    </div>
                    <Card>
                        <CardMedia className="rounded-full p-3" component="img" image={require('../assets/images/teeth.jpg')} />
                        <CardContent>
                            <Typography variant="h5">{patient ? patient.firstName + ' ' + patient.middleName + ' ' + patient.lastName : "---"}</Typography>
                            <div className="flex flex-col">
                                <Typography><span className="font-bold">Date of Birth</span>: {patient ? new Date(patient.dateOfBirth).toDateString() : "---"}</Typography>
                                <Typography><span className="font-bold">Phone Number</span>: {patient ? patient.phoneNumber : "---"}</Typography>
                                <Typography><span className="font-bold">Branch</span>: {patient ? patient.branchName : "---"}</Typography>
                                <Typography><span className="font-bold">Gender</span>: {patient ? patient.gender : "---"}</Typography>
                                <Typography><span className="font-bold">Registered Date</span>: {patient ? new Date(patient.createdAt).toDateString() : "---"}</Typography>
                                <Typography><span className="font-bold">Card No</span>: {patient ? patient.cardNo : "---"}</Typography>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
        <Dialog open={open} onClose={() => setOpen(false)}>
            <DialogContent>
                <Typography align="center" fontWeight={"bold"}>Patient History</Typography>
                <form className="py-3" onSubmit={handleSubmit(OnSubmit)}>
                    <div className="grid grid-cols-2 gap-2">
                        <input type="hidden" {...register("patientId")} value={patient?.id} />
                        <TextareaAutosize className="bg-transparent" placeholder="CC" {...register("cc")} />
                        <TextareaAutosize className="bg-transparent" placeholder="HPI" {...register("hpi")} />
                        <TextareaAutosize className="bg-transparent" placeholder="PMHY" {...register("pmhy")} />
                        <TextareaAutosize className="bg-transparent" placeholder="PDMY" {...register("pdhy")} />
                        <TextareaAutosize className="bg-transparent" placeholder="GA" {...register("ga")} />
                        <TextareaAutosize className="bg-transparent" placeholder="IO" {...register("io")} />
                        <TextareaAutosize className="bg-transparent" placeholder="EO" {...register("eo")} />
                        <TextareaAutosize className="bg-transparent" placeholder="Asst" {...register("asst")} />
                        <TextareaAutosize className="bg-transparent" placeholder="TyPlan" {...register("tyPlan")} />
                        <Button onClick={() => setOpen(false)} type="submit" className="col-span-2" variant="contained">Add History</Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
        <Dialog open={open2} onClose={() => setOpen2(false)}>
            <DialogTitle align="center">Add Card Fee</DialogTitle>
            <DialogContent>
                <form className="grid grid-cols-1 gap-3 pt-3" onSubmit={handleSubmit1(onSubmit1)}>
                    <input type="hidden" {...register1("toWhom")} value={patient?.id} />
                    <div>
                        <TextField size="small" label="Amount" fullWidth type="number" {...register1("amount")}
                            error={!!errors.amount} inputProps={{step: "0.01"}} />
                        <p className="text-sm text-red-700">{errors.amount?.message}</p>
                    </div>
                    <div>
                        <TextField size="small" label="Description" fullWidth type="text" {...register1("description")}
                            error={!!errors.description} />
                        <p className="text-sm text-red-700">{errors.description?.message}</p>
                    </div>
                    <div>
                        <TextField size="small" label="Purpose" fullWidth type="text" {...register1("purpose")} error={!!errors.purpose} />
                        <p className="text-sm text-red-700">{errors.purpose?.message}</p>
                    </div>
                    <Button type="submit" variant="contained">Add Card Fee</Button>
                </form>
            </DialogContent>
        </Dialog>
    </>
};

export default SinglePatient;
